import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { db } from '@/lib/db';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { dateTime, humanise, relativeDays } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusChip } from '@/components/ui/StatusChip';
import { CheckboxInput, SelectInput, TextInput } from '@/components/ui/Field';
import type { Profile, Site, UserRole } from '@/types/models';
import { useRows } from '@/hooks/useLive';
import { MODULES, defaultModules, type ModuleKey } from '@/lib/access';
import { QUARRY_SITE_ID } from '@/lib/fleet';

/**
 * Users and roles.
 *
 * The list comes from list_users() rather than the profiles table, because
 * it also carries the parts of auth.users a manager needs — the sign-in
 * address, whether an invitation is still outstanding, when they last
 * signed in. That function checks the caller's role itself, so an operator
 * who reaches this URL gets an empty list, not a directory of colleagues'
 * email addresses.
 *
 * Everything that needs the Supabase Admin API — creating an account,
 * sending an invitation, removing a sign-in — goes through the manage-users
 * Edge Function. The service_role key it needs must never reach a browser.
 */

interface ListedUser extends Record<string, unknown> {
  id: string;
  full_name: string;
  email: string | null;
  employee_no: string | null;
  phone: string | null;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed: boolean;
  invited: boolean;
  must_change_password: boolean;
  has_activity: boolean;
  /** Null for anyone who sees the quarry; the primary branch otherwise. */
  home_site_id: string | null;
  /** 0055: every site, or the ones in site_ids. */
  all_sites?: boolean;
  site_ids?: string[];
  /** 0055: custom module list; null = the role's defaults. */
  modules?: string[] | null;
}

const ROLES: { value: UserRole; label: string; note: string }[] = [
  { value: 'operator', label: 'Operator', note: 'Captures machine hours, loads and activities on their own diary entries.' },
  { value: 'supervisor', label: 'Supervisor', note: 'Reviews and corrects diaries, approves them, enters production, blasts, weather and breakdowns.' },
  { value: 'maintenance', label: 'Maintenance', note: 'Breakdowns, services, liners, parts and stock.' },
  { value: 'manager', label: 'Manager', note: 'Everything operational, plus targets, reports, master data and the audit trail.' },
  { value: 'admin', label: 'Administrator', note: 'Everything, including user management and system settings.' },
];

const ROLE_TONE: Record<UserRole, 'grey' | 'blue' | 'amber' | 'green'> = {
  operator: 'grey',
  supervisor: 'blue',
  maintenance: 'amber',
  manager: 'green',
  admin: 'green',
};

const EMPTY = {
  email: '', full_name: '', employee_no: '', phone: '', role: 'operator' as UserRole,
  password: '', confirm: '', mustChange: true,
  // 0055 access
  allSites: true, sites: [] as string[], primary: '', sameAsRole: true, modules: [] as string[],
};

/** The sites in a user's access, as the database keeps them (0055). */
function sitesOf(u: ListedUser): string[] {
  const list = [...(u.site_ids ?? [])];
  if (u.home_site_id && !list.includes(u.home_site_id)) list.push(u.home_site_id);
  // Before 0055, or nothing assigned: no home branch means the quarry.
  if (!list.length && !u.home_site_id) list.push(QUARRY_SITE_ID);
  return list;
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/** Roles a branch user can hold (0035): no workshop role, no administrator. */
const BRANCH_ROLES: UserRole[] = ['operator', 'supervisor', 'manager'];

const MIN_PASSWORD = 8;

/** How the account gets its first password. */
type AddMode = 'invite' | 'manual';

/** Call the Edge Function with the signed-in user's own token. */
async function manage(action: string, payload: Record<string, unknown>) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Your session has expired — sign in again.');

  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const res = await fetch(`${base}/functions/v1/manage-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    },
    body: JSON.stringify({ action, ...payload }),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      body.error
        ?? (res.status === 404
          ? 'The manage-users function is not deployed yet — see docs/11-users.md.'
          : `The server refused the request (${res.status}).`),
    );
  }
  return body as Record<string, unknown>;
}

/** The modules a branch-only user can open: the quarry's screens are closed
 *  to them (QuarryOnly), so ticking those would show nothing. */
const BRANCH_USABLE: ModuleKey[] = ['diary', 'concrete', 'fleet', 'vehicle_checks', 'breakdowns', 'fuel', 'brick', 'precast'];

export function UsersPage() {
  const isAdmin = useAuthStore((s) => s.hasRole('admin'));
  const me = useAuthStore((s) => s.profile);
  const sites = useRows<Site>('sites', 'name');
  const siteName = (id: string | null) =>
    id ? (sites ?? []).find((x) => x.id === id)?.name ?? 'Branch' : 'Quarry';

  const [rows, setRows] = useState<ListedUser[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editing, setEditing] = useState<ListedUser | null>(null);
  const [inviting, setInviting] = useState(false);
  const [mode, setMode] = useState<AddMode>('invite');
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('list_users');
    if (error) {
      setLoadError(
        error.code === '42883'
          ? 'The user list needs migration 0023 — run "npx supabase db push".'
          : error.message,
      );
      setRows([]);
      return;
    }
    setLoadError(null);
    setRows((data ?? []) as ListedUser[]);
  }, []);

  useEffect(() => { void load(); }, [load]);

  function open(u: ListedUser) {
    setEditing(u);
    setConfirmRemove(false);
    setForm({
      ...EMPTY,
      email: u.email ?? '',
      full_name: u.full_name,
      employee_no: u.employee_no ?? '',
      phone: u.phone ?? '',
      role: u.role,
      allSites: u.all_sites ?? !u.home_site_id,
      sites: sitesOf(u),
      primary: u.home_site_id ?? '',
      sameAsRole: !u.modules,
      modules: u.modules ?? [],
    });
  }

  function openInvite() {
    setInviting(true);
    setConfirmRemove(false);
    setMode('invite');
    setForm(EMPTY);
  }

  /** Anything that talks to the server, with one place for the failure. */
  async function run(what: string, fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      const e = err as { message?: string; code?: string };
      toast.error(
        e.code === '42501' || e.message?.includes('administrator')
          ? e.message ?? 'Only an administrator may do that.'
          : e.message ?? `Could not ${what}`,
      );
    } finally {
      setBusy(false);
    }
  }

  const onAdd = () => run(mode === 'invite' ? 'send the invitation' : 'create the account', async () => {
    if (!form.email.includes('@')) throw new Error('A valid email address is required.');
    if (!form.full_name.trim()) throw new Error('A name is required.');

    const common = {
      email: form.email.trim(),
      full_name: form.full_name.trim(),
      employee_no: form.employee_no.trim() || null,
      phone: form.phone.trim() || null,
      role: form.role,
    };

    if (mode === 'invite') {
      await manage('invite', common);
      toast.success(`Invitation sent to ${common.email}`);
    } else {
      if (form.password.length < MIN_PASSWORD) {
        throw new Error(`The password must be at least ${MIN_PASSWORD} characters.`);
      }
      if (form.password !== form.confirm) throw new Error('The two passwords do not match.');

      await manage('create', {
        ...common,
        password: form.password,
        must_change_password: form.mustChange,
      });
      toast.success(`${common.full_name} can sign in now`);
    }

    setInviting(false);
    setForm(EMPTY);
    await load();
  });

  const onSave = () => run('save', async () => {
    if (!editing) return;

    const details: Record<string, string | null> = {};
    if (form.full_name !== editing.full_name) details.full_name = form.full_name.trim();
    if ((form.employee_no || null) !== editing.employee_no) details.employee_no = form.employee_no.trim() || null;
    if ((form.phone || null) !== editing.phone) details.phone = form.phone.trim() || null;

    if (Object.keys(details).length > 0) {
      const { error } = await supabase.from('profiles').update(details).eq('id', editing.id);
      if (error) throw error;
    }

    const setRole = async () => {
      if (form.role === editing.role) return;
      // Roles change only through the admin-gated RPC — the authenticated
      // role holds no column privilege on profiles.role.
      const { data, error } = await supabase.rpc('set_user_role', {
        p_user_id: editing.id, p_role: form.role,
      });
      if (error) throw error;
      if (data) await db.profiles.put(data as Profile);
    };
    const branchOnly = !form.allSites && !form.sites.includes(QUARRY_SITE_ID);
    const modules = form.sameAsRole ? null : form.modules;
    const before = {
      all: editing.all_sites ?? !editing.home_site_id,
      sites: sitesOf(editing),
      primary: editing.home_site_id ?? '',
      modules: editing.modules ?? null,
    };
    const accessChanged = form.allSites !== before.all
      || (!form.allSites && (!sameSet(form.sites, before.sites)
        || (branchOnly && form.primary !== before.primary)))
      || JSON.stringify(modules ? [...modules].sort() : null)
        !== JSON.stringify(before.modules ? [...before.modules].sort() : null);
    const setAccess = async () => {
      if (!accessChanged) return;
      const { data, error } = await supabase.rpc('set_user_access', {
        p_user_id: editing.id,
        p_all_sites: form.allSites,
        p_sites: form.allSites ? [] : form.sites,
        p_primary: branchOnly ? (form.primary || form.sites[0]) : null,
        p_modules: modules,
      });
      if (error) throw error;
      if (data) await db.profiles.put(data as Profile);
    };
    // The database checks the pair: a branch-only user is an operator,
    // supervisor or manager. So the order depends on the direction.
    if (branchOnly) { await setRole(); await setAccess(); } else { await setAccess(); await setRole(); }

    toast.success(`${form.full_name} updated`);
    setEditing(null);
    await load();
  });

  const onToggleActive = () => run('change the status', async () => {
    if (!editing) return;
    const { data, error } = await supabase.rpc('set_user_active', {
      p_user_id: editing.id, p_active: !editing.is_active,
    });
    if (error) throw error;
    if (data) await db.profiles.put(data as Profile);
    toast.success(`${editing.full_name} ${editing.is_active ? 'deactivated' : 'reactivated'}`);
    setEditing(null);
    await load();
  });

  const onResend = () => run('send the invitation', async () => {
    if (!editing) return;
    await manage(editing.invited ? 'resend' : 'reset', { user_id: editing.id });
    toast.success(
      editing.invited
        ? `Invitation sent again to ${editing.email}`
        : `Password reset sent to ${editing.email}`,
    );
  });

  const onRemove = () => run('remove the user', async () => {
    if (!editing) return;
    const result = await manage('remove', { user_id: editing.id });
    toast.success(
      result.status === 'deleted'
        ? `${editing.full_name} deleted`
        : `${editing.full_name} removed — their recorded work is kept`,
    );
    setEditing(null);
    await load();
  });

  const branchOnlyForm = !form.allSites && !form.sites.includes(QUARRY_SITE_ID);
  // The quarry and every branch; a deactivated one only if already ticked.
  const assignable = (sites ?? []).filter((x) => !x.deleted_at
    && (x.kind === 'quarry' || x.kind === 'branch')
    && (x.is_active || form.sites.includes(x.id)));
  const roleDefaults = (role: UserRole): ModuleKey[] => {
    const quarry = form.allSites || form.sites.includes(QUARRY_SITE_ID);
    const branch = !form.allSites && form.sites.some((id) => id !== QUARRY_SITE_ID);
    return defaultModules(role, quarry, branch);
  };

  const columns: Column<ListedUser>[] = [
    { key: 'full_name', label: 'Name' },
    { key: 'email', label: 'Sign-in address', render: (u) => u.email ?? '—' },
    {
      key: 'role', label: 'Role',
      render: (u) => <StatusChip tone={ROLE_TONE[u.role]} dot={false}>{humanise(u.role)}</StatusChip>,
    },
    {
      key: 'home_site_id', label: 'Branches',
      value: (u) => ((u.all_sites ?? !u.home_site_id) ? 'All branches'
        : sitesOf(u).map((id) => siteName(id)).join(', ')),
      render: (u) => ((u.all_sites ?? !u.home_site_id)
        ? <span className="muted">All branches</span>
        : <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
            {sitesOf(u).map((id) => (
              <StatusChip key={id} tone={id === QUARRY_SITE_ID ? 'grey' : 'blue'} dot={false}>
                {siteName(id)}
              </StatusChip>))}
          </span>),
    },
    {
      key: 'modules', label: 'Modules',
      value: (u) => (u.modules ? `${u.modules.length} chosen` : 'role'),
      render: (u) => (u.modules
        ? <StatusChip tone="amber" dot={false}>{u.modules.length} chosen</StatusChip>
        : <span className="muted">Same as role</span>),
    },
    { key: 'employee_no', label: 'Employee no.' },
    {
      key: 'is_active', label: 'Status', align: 'center',
      value: (u) => (!u.is_active ? 'inactive'
        : u.must_change_password ? 'new password'
          : u.invited ? 'invited' : 'active'),
      render: (u) =>
        !u.is_active ? <StatusChip tone="grey">Inactive</StatusChip>
          // A password an administrator typed is not yet the user's own, and
          // the register should say so until it is.
          : u.must_change_password ? <StatusChip tone="amber">Must change password</StatusChip>
            : u.invited ? <StatusChip tone="amber">Invited</StatusChip>
              : <StatusChip tone="green">Active</StatusChip>,
    },
    {
      key: 'last_sign_in_at', label: 'Last signed in',
      render: (u) => u.last_sign_in_at
        ? <span title={dateTime(u.last_sign_in_at)}>{relativeDays(u.last_sign_in_at)}</span>
        : <span className="muted">never</span>,
    },
  ];

  const selfEdit = editing?.id === me?.id;

  return (
    <>
      <Panel
        title="Users & roles"
        flush
        actions={isAdmin ? <Button onClick={openInvite}>+ Add user</Button> : undefined}
      >
        <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }} className="small muted">
          {isAdmin
            ? <>Add someone either by emailing them an invitation to set their own password, or by
                typing a password yourself and handing it over — the second needs no mailbox and no
                mail server, which is what an operator at the face usually has.</>
            : <>Only an administrator can add or remove people, or change a role.</>}
        </div>

        {loadError && (
          <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }}>
            <div className="login__error">{loadError}</div>
          </div>
        )}

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(u) => u.id}
          onRowClick={isAdmin ? open : undefined}
          defaultSort="full_name"
          searchPlaceholder="Search users…"
          empty="No users yet."
        />
      </Panel>

      {/* ---------------------------------------------------------------- */}
      <Drawer
        open={inviting}
        title="Add user"
        onClose={() => setInviting(false)}
        footer={
          <>
            <span className="spacer" />
            <Button variant="secondary" onClick={() => setInviting(false)} disabled={busy}>Cancel</Button>
            <Button onClick={onAdd} loading={busy}>
              {mode === 'invite' ? 'Send invitation' : 'Create account'}
            </Button>
          </>
        }
      >
        <div className="row" style={{ gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
          <Button
            variant={mode === 'invite' ? 'primary' : 'secondary'}
            onClick={() => setMode('invite')}
          >
            Email an invitation
          </Button>
          <Button
            variant={mode === 'manual' ? 'primary' : 'secondary'}
            onClick={() => setMode('manual')}
          >
            Set a password now
          </Button>
        </div>
        <TextInput id="i-email" label="Email address" type="email" required
          value={form.email} onChange={(v) => setForm({ ...form, email: v })}
          help={mode === 'invite'
            ? 'Where the invitation goes. This is also what they sign in with.'
            : 'What they sign in with. It does not have to be a real mailbox — '
              + 'for an operator without email, something like jonas@suremix.local works.'} />

        <TextInput id="i-name" label="Full name" required
          value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />

        <TextInput id="i-empno" label="Employee no."
          value={form.employee_no} onChange={(v) => setForm({ ...form, employee_no: v })} />

        <TextInput id="i-phone" label="Phone" type="tel"
          value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />

        <SelectInput id="i-role" label="Role" required
          value={form.role}
          options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
          onChange={(v) => setForm({ ...form, role: (v as UserRole) ?? 'operator' })}
          help={ROLES.find((r) => r.value === form.role)?.note} />

        {mode === 'manual' && (
          <>
            <div className="form-section">Password</div>

            <TextInput id="i-password" label="Password" type="password" required
              value={form.password} onChange={(v) => setForm({ ...form, password: v })}
              help={`At least ${MIN_PASSWORD} characters. Write it down for them, or read it out — `
                + 'it is not stored anywhere you can look it up again.'} />

            <TextInput id="i-password2" label="Repeat it" type="password" required
              value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} />

            <CheckboxInput
              id="i-mustchange"
              label="Make them choose their own password at first sign-in"
              value={form.mustChange}
              onChange={(v) => setForm({ ...form, mustChange: v })}
              help={'Recommended. Until they do, this is a password you also know — and '
                + 'everything they capture is recorded under their name.'}
            />
          </>
        )}

        <div className="form-section">What happens next</div>
        {mode === 'invite' ? (
          <ul className="small muted" style={{ paddingLeft: '1.1em', margin: 0 }}>
            <li style={{ marginBottom: 6 }}>The account is created with no password at all.</li>
            <li style={{ marginBottom: 6 }}>
              They get an email with a link, valid for 24 hours, and choose their own password.
            </li>
            <li style={{ marginBottom: 6 }}>
              Until they follow it the row shows <strong>Invited</strong>, and the link can be sent again.
            </li>
            <li>
              Mail goes through the same server as the daily report. If that is not set up yet,
              Administration → Report delivery is where it lives.
            </li>
          </ul>
        ) : (
          <ul className="small muted" style={{ paddingLeft: '1.1em', margin: 0 }}>
            <li style={{ marginBottom: 6 }}>
              The account works immediately. No email is sent and no mail server is needed.
            </li>
            <li style={{ marginBottom: 6 }}>
              Hand them the password yourself — it is used once to create the account and is
              not kept anywhere.
            </li>
            <li>
              {form.mustChange
                ? <>They will be asked to choose their own password the first time they sign in,
                    and cannot use the app until they do. The row shows{' '}
                    <strong>Must change password</strong> until then.</>
                : <>They will keep the password you typed. You will know it too, so anything
                    captured under their name could also have been captured by you — worth
                    avoiding for anyone whose diary entries matter.</>}
            </li>
          </ul>
        )}
      </Drawer>

      {/* ---------------------------------------------------------------- */}
      <Drawer
        open={editing !== null}
        title="Edit user"
        subtitle={editing?.email ?? editing?.full_name}
        onClose={() => setEditing(null)}
        footer={
          <>
            {editing && !selfEdit && (
              <Button variant="secondary" onClick={onToggleActive} disabled={busy}>
                {editing.is_active ? 'Deactivate' : 'Reactivate'}
              </Button>
            )}
            <span className="spacer" />
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={onSave} loading={busy}>Save</Button>
          </>
        }
      >
        <TextInput id="u-name" label="Full name" required
          value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />

        <TextInput id="u-empno" label="Employee no."
          value={form.employee_no} onChange={(v) => setForm({ ...form, employee_no: v })} />

        <TextInput id="u-phone" label="Phone" type="tel"
          value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />

        <SelectInput id="u-role" label="Role" required
          value={form.role}
          options={ROLES.filter((r) => !branchOnlyForm || BRANCH_ROLES.includes(r.value))
            .map((r) => ({ value: r.value, label: r.label }))}
          onChange={(v) => {
            const role = (v as UserRole) ?? 'operator';
            setForm({ ...form, role, modules: form.sameAsRole ? roleDefaults(role) : form.modules });
          }}
          help={branchOnlyForm
            ? {
                operator: 'Captures deliveries for the branch and corrects their own.',
                supervisor: 'Captures and corrects any delivery at the branch; adds customers.',
                manager: 'Everything at the branch, plus its monthly target and the mix and reason lists.',
              }[form.role as 'operator' | 'supervisor' | 'manager']
            : ROLES.find((r) => r.value === form.role)?.note} />

        <div className="form-section">Branches</div>
        {selfEdit && (
          <p className="small muted" style={{ marginTop: 0 }}>
            Your own branches and modules are changed by another administrator.
          </p>
        )}
        <CheckboxInput id="u-all" label="All branches"
          value={form.allSites}
          disabled={selfEdit || form.role === 'admin'}
          onChange={(v) => setForm({ ...form, allSites: v,
            sites: v ? form.sites : (form.sites.length ? form.sites : [QUARRY_SITE_ID]) })}
          help={form.role === 'admin'
            ? 'An administrator works across the company.'
            : 'Sees the quarry and every branch, including branches added later.'} />
        {!form.allSites && (
          <div className="access-list">
            {assignable.map((x) => (
              <CheckboxInput key={x.id} id={`u-site-${x.id}`}
                label={`${x.name}${x.kind === 'quarry' ? ' (quarry)' : ''}${x.is_active ? '' : ' — deactivated'}`}
                value={form.sites.includes(x.id)}
                disabled={selfEdit || (!x.is_active && !form.sites.includes(x.id))}
                onChange={(on) => {
                  const next = on ? [...form.sites, x.id] : form.sites.filter((id) => id !== x.id);
                  const nextBranchOnly = !next.includes(QUARRY_SITE_ID);
                  setForm({
                    ...form,
                    sites: next,
                    primary: next.includes(form.primary) ? form.primary : (next.find((id) => id !== QUARRY_SITE_ID) ?? ''),
                    // Branch-only: keep a role a branch can hold.
                    role: nextBranchOnly && !BRANCH_ROLES.includes(form.role) ? 'operator' : form.role,
                  });
                }} />
            ))}
            {form.sites.length === 0 && (
              <div className="login__error">Tick at least one, or All branches.</div>
            )}
          </div>
        )}
        {branchOnlyForm && form.sites.length > 1 && (
          <SelectInput id="u-primary" label="Starts in" required
            value={form.primary || form.sites[0]}
            options={form.sites.map((id) => ({ value: id, label: siteName(id) }))}
            disabled={selfEdit}
            onChange={(v) => setForm({ ...form, primary: v ?? '' })}
            help="The branch the app opens in. They switch between their branches in the menu." />
        )}
        <p className="small muted" style={{ marginTop: 'var(--sp-2)' }}>
          {form.allSites
            ? 'Quarry managers and administrators also see the branches\' concrete and fuel; other roles see the quarry\'s, plus what every branch shares (fleet, breakdowns, checks).'
            : branchOnlyForm
              ? 'Sees only the ticked branches, and nothing of the quarry.'
              : 'Sees the quarry and the ticked branches only.'}
        </p>

        <div className="form-section">Modules</div>
        <CheckboxInput id="u-same" label="Same as role"
          value={form.sameAsRole}
          disabled={selfEdit || form.role === 'admin'}
          onChange={(v) => setForm({ ...form, sameAsRole: v, modules: v ? [] : roleDefaults(form.role) })}
          help={form.role === 'admin'
            ? 'An administrator has every module.'
            : `The menu ${/^[aeiou]/.test(form.role) ? 'an' : 'a'} ${form.role} has always had. Untick to choose modules one by one.`} />
        <div className="access-list">
          {MODULES.filter((m) => !branchOnlyForm || BRANCH_USABLE.includes(m.key)).map((m) => {
            const on = form.sameAsRole ? roleDefaults(form.role).includes(m.key) : form.modules.includes(m.key);
            return (
              <CheckboxInput key={m.key} id={`u-mod-${m.key}`} label={m.label}
                value={on}
                disabled={selfEdit || form.sameAsRole || form.role === 'admin'}
                onChange={(v) => setForm({ ...form,
                  modules: v ? [...form.modules, m.key] : form.modules.filter((x) => x !== m.key) })} />
            );
          })}
        </div>
        <p className="small muted" style={{ marginTop: 'var(--sp-2)' }}>
          A module opens its screens and its information. What the user may change there still
          follows their role. Administration also needs the manager or administrator role.
          {branchOnlyForm && ' A user who sees only branches has the branch screens listed here; the quarry\'s screens (dashboard, workshop, reports…) need the quarry ticked above.'}
        </p>

        {selfEdit && form.role !== editing?.role && (
          <div className="login__error">
            You are changing your own role. Reducing it will remove your access to this screen.
          </div>
        )}

        {editing && (
          <>
            <div className="form-section">Sign-in</div>
            <p className="small muted" style={{ marginTop: 0 }}>
              {editing.invited
                ? 'This invitation has not been used yet — the account cannot sign in until it is.'
                : editing.last_sign_in_at
                  ? `Last signed in ${relativeDays(editing.last_sign_in_at)} · ${dateTime(editing.last_sign_in_at)}.`
                  : 'This account has never signed in.'}
            </p>
            <Button variant="secondary" onClick={onResend} disabled={busy || !editing.email}>
              {editing.invited ? 'Send the invitation again' : 'Send a password reset'}
            </Button>

            {!selfEdit && (
              <>
                <div className="form-section">Remove</div>
                {editing.has_activity ? (
                  <p className="small muted" style={{ marginTop: 0 }}>
                    This person has captured work. Removing them ends their sign-in and takes
                    them off this list, but their diaries, breakdowns and tickets stay exactly
                    as they are — an audit trail that points at nobody is worse than none.
                    Deactivating is usually the right choice.
                  </p>
                ) : (
                  <p className="small muted" style={{ marginTop: 0 }}>
                    This account has recorded nothing, so it can be deleted outright.
                  </p>
                )}

                {confirmRemove ? (
                  <div className="row" style={{ gap: 'var(--sp-2)' }}>
                    <Button variant="danger" onClick={onRemove} loading={busy}>
                      Yes, remove {editing.full_name}
                    </Button>
                    <Button variant="secondary" onClick={() => setConfirmRemove(false)} disabled={busy}>
                      Keep
                    </Button>
                  </div>
                ) : (
                  <Button variant="secondary" onClick={() => setConfirmRemove(true)} disabled={busy}>
                    {editing.has_activity ? 'Remove user' : 'Delete account'}
                  </Button>
                )}
              </>
            )}
          </>
        )}

        <div className="form-section">What each role can do</div>
        <ul className="small muted" style={{ paddingLeft: '1.1em', margin: 0 }}>
          {ROLES.map((r) => (
            <li key={r.value} style={{ marginBottom: 6 }}>
              <strong>{r.label}</strong> — {r.note}
            </li>
          ))}
        </ul>
      </Drawer>
    </>
  );
}
